import React, {useMemo, useRef, useState} from 'react';
import tw from 'twin.macro';
import auditUpload from '@/api/auditUpload';
import {
    TTDUpload,
    TTDUploadStatus,
    useFileUploadStatusSubscription,
    useGetFileUploads
} from '@/api/graphql/subscriptions/subscribeFileUploads';
import {BaseButtonStyle, FileUploadsTableStyle} from '@/components/dashboard/styled';
import downloadUploadResults from '@/api/downloadUploadResults';
import {format} from 'date-fns';


export default () => {
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ message, setMessage ] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const [ downloadingId, setDownloadingId ] = useState<number | null>(null);

    const [ fileUploads, refetch ] = useGetFileUploads();
    useFileUploadStatusSubscription(() => refetch());

    const isProcessing = useMemo(() => {
        if (loading) return true;

        return !!fileUploads.find(o => o.status === 'Processing');
    }, [ fileUploads ]);


    const uploadFile = async () => {
        if (!fileRef.current || !fileRef.current.files?.[0]) return;

        const file = fileRef.current.files[0];
        try {
            const res = await auditUpload(file);
            setMessage('Upload started successfully.');

        } catch (err) {
            console.error('uploadFile err: ', err);
            setMessage('Network error during upload.');
        } finally {
            setLoading(false);
        }
    };

    const onFileUploadCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMessage('');
        const target = e.currentTarget.files?.[0];
        if (!target) return;

        setLoading(true);
        uploadFile();
    };

    const onDownloadUploadResults = (id: number, outputFile: string) => {
        setDownloadingId(id);
        downloadUploadResults(outputFile)
            .catch(err => console.log('onDownloadUploadResults err: ', err))
            .then(() => {
                setDownloadingId(null);
            });
    };

    const formatUploadStatus = (o: TTDUpload) => {
        if (o.status !== TTDUploadStatus.Processing) return o.status;

        if (o.total_rows) return `${TTDUploadStatus.Processing} (${o.processed_count} of ${o.total_rows} completed)`;
    };

    const renderFileUploadRow = (o: TTDUpload, i: number) => {
        const isDownloading = downloadingId === o.id;

        return (
            <tr key={o.id}>
                <td>
                    <span>{o.file_name}</span>
                </td>
                <td>
                    <span>{format(new Date(o.upload_date), 'M/d/yy h:mmaaa')}</span>
                </td>

                <td>
                    {
                        o.output_file && o.status === TTDUploadStatus.Completed ?
                            <a
                                css={[ tw`text-link cursor-pointer`, isDownloading && tw`opacity-50 pointer-events-none` ]}
                                onClick={() => !isDownloading && onDownloadUploadResults(o.id, o.output_file)}
                            >
                                {isDownloading ? 'Downloading...' : 'Download'}
                            </a>
                            : <span css={[ tw`italic` ]}>{formatUploadStatus(o)} {o.errors?.length ? o.errors : ''}</span>
                    }
                </td>
            </tr>
        );
    };

    return (
        <>
            <BaseButtonStyle css={[ tw`bg-indigo-700 text-white` ]} onClick={() => fileRef.current?.click()} disabled={isProcessing}>Upload CSV</BaseButtonStyle>

            <input
                ref={fileRef}
                type="file"
                css={[ tw`hidden` ]}
                accept=".csv"
                onChange={onFileUploadCapture}
            />
            {message && <div css={[ tw`mt-2 text-sm text-gray-700` ]}>{message}</div>}

            <p css={[ tw`mt-2 text-sm text-gray-600` ]}>
                CSV should contain <strong>exactly two columns</strong> with headers: <code>student_id</code> and <code>term</code>.
            </p>

            {
                fileUploads.length ?
                    <div css={[ tw`my-6` ]}>
                        <h3 css={[ tw`text-lg font-medium  mb-2` ]}>Upload History</h3>
                        <FileUploadsTableStyle css={[ tw`flex-shrink-0` ]}>
                            <thead>
                            <tr>
                                <th>
                                    <span>File Name</span>
                                </th>
                                <th>
                                    <span>Uploaded</span>
                                </th>
                                <th>
                                    <span>Results</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            {
                                fileUploads.map((o, i) => {
                                    return renderFileUploadRow(o, i);
                                })
                            }
                            </tbody>
                        </FileUploadsTableStyle>
                    </div>
                    :
                    null
            }



        </>
    );
};
